import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import classNames, { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { FormHTMLAttributes, KeyboardEvent, PropsWithChildren, ReactNode } from 'react';

import styles from './PageOverlay.module.css';

import { OverlayCloseButton } from 'components/OverlayCloseButton';
import { Spinner } from 'components/Spinner';
import { Backdrop } from 'components/Backdrop';

export interface PageOverlayProps {
  /** The buttons of the dialog. */
  buttons?: ReactNode;
  /** `true` to show a spinner, if the contents of the dialog is lazy loaded; otherwise, `false`. */
  isLoading?: boolean;
  /** The `aria-valuetext` value of the [Spinner](/story/atoms-spinner--spinner-example) component. Used together with `isLoading`. */
  spinnerAriaValueText?: string;
  /** The `aria-label` value of the [OverlayCloseButton](/story/atoms-overlayclosebutton--overlay-close-button-example) component. */
  closeButtonAriaValueText?: string;
  /** `true` to render the dialog opened; otherwise, `false`. */
  isOpen: boolean;
  /** `true` to disable form validation; otherwise, `false`. */
  noValidate?: boolean;
  /** The callback function that is invoked when the dialog is closed. */
  onClose?(): void;
  /** The callback function that is invoked when the form is submitted. */
  onSubmit?: FormHTMLAttributes<HTMLFormElement>['onSubmit'];
  /** The title of the dialog. */
  title?: string;
  /** The contents of the dialog header. */
  headerContent?: ReactNode;
  /** The variant styling of the dialog. */
  variant?: 'full-screen' | 'with-gutters';
  /** The content background color of the dialog. Default grey-300*/
  contentBackgroundColor?: 'grey' | 'white';
}

/** Represents a component for a modal dialog. */
export function PageOverlay({
  buttons,
  children,
  isLoading,
  spinnerAriaValueText = 'Loading',
  closeButtonAriaValueText = 'Close',
  isOpen,
  noValidate = false,
  onClose,
  onSubmit,
  title,
  headerContent,
  variant = 'full-screen',
  contentBackgroundColor = 'grey',
}: PropsWithChildren<PageOverlayProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { dialogProps, titleProps } = useDialog({ role: 'dialog' }, ref);
  const { modalProps } = useModal({ isDisabled: !isOpen });
  const { overlayProps, underlayProps } = useOverlay({ isOpen, onClose }, ref);

  usePreventScroll({ isDisabled: !isOpen });

  useEffect(() => {
    const formElement = formRef.current;

    if (!formElement) return;

    function checkScrollable() {
      const scrollHeight = formElement!.scrollHeight;
      const clientHeight = formElement!.clientHeight;
      const scrollTop = formElement!.scrollTop;

      setIsScrollable(scrollHeight > clientHeight);
      setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight);
    }

    function handleScrollAndResize() {
      checkScrollable();
    }

    formElement.addEventListener('scroll', handleScrollAndResize);
    window.addEventListener('resize', handleScrollAndResize);

    const timeoutId = setTimeout(() => {
      checkScrollable();
    }, 0);

    return () => {
      formElement.removeEventListener('scroll', handleScrollAndResize);

      window.removeEventListener('resize', handleScrollAndResize);
      clearTimeout(timeoutId);
    };
  }, [isOpen, headerContent]);

  function onKeyUp(event: KeyboardEvent) {
    event.stopPropagation();
  }

  if (!isOpen) return null;

  return (
    <OverlayContainer className={styles['page-overlay__container']}>
      <Backdrop {...underlayProps}>
        {isLoading ? (
          <Spinner ariaValueText={spinnerAriaValueText} />
        ) : (
          <>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <FocusScope autoFocus contain restoreFocus>
              <div
                {...dialogProps}
                {...modalProps}
                {...overlayProps}
                className={clsx(styles['page-overlay'], {
                  [styles[`page-overlay--${variant}`]]: variant,
                })}
                onKeyUp={onKeyUp}
                role="presentation"
                ref={ref}
              >
                {(title || headerContent) && (
                  <div className={styles['page-overlay__title-wrapper']}>
                    {title && (
                      <h1 {...titleProps} className={styles['page-overlay__title']}>
                        {title}
                      </h1>
                    )}
                    {headerContent}
                    {
                      <span className={styles['page-overlay__close']}>
                        <OverlayCloseButton aria-label={closeButtonAriaValueText} onClick={onClose} />
                      </span>
                    }
                  </div>
                )}
                <form
                  className={classNames(
                    styles['page-overlay__content'],
                    styles[`page-overlay__content--${contentBackgroundColor}`],
                    {
                      [styles['page-overlay--content-with-buttons']]: buttons,
                    }
                  )}
                  onSubmit={onSubmit}
                  noValidate={noValidate}
                  ref={formRef}
                >
                  <div className={styles['page-overlay__children']}>{children}</div>
                  {buttons && (
                    <div
                      className={classNames(styles['page-overlay__buttons-bar'], {
                        [styles['page-overlay__buttons-bar--shadow']]: isScrollable && !isScrolledToBottom,
                      })}
                    >
                      {buttons}
                    </div>
                  )}
                </form>
              </div>
            </FocusScope>
          </>
        )}
      </Backdrop>
    </OverlayContainer>
  );
}
